import { debounce } from 'lodash';
import { useRef } from 'react';
import styled from 'styled-components';

import { AppHeader } from '@hivebrite/design-system/AppHeader';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Card } from '@hivebrite/design-system/Card';
import { Container } from '@hivebrite/design-system/Container';
import { Link } from '@hivebrite/design-system/Link';

import FiltersApp from 'hb-react/frontoffice/apps/Filters/containers/FiltersApp';
import { useSessionInfoContext } from 'hb-react/frontoffice/contexts/SessionInfoContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Membership from 'hb-react/shared/apps/Memberships/components/Membership';
import MemberShipTags from 'hb-react/shared/apps/Memberships/components/MemberShipTags';
import NoMemberships from 'hb-react/shared/apps/Memberships/components/NoMemberships';
import { MEMBERSHIP_PURCHASE_PROCESS_TYPES } from 'hb-react/shared/apps/Memberships/constants';
import useMembershipsHeaderProps from 'hb-react/shared/apps/Memberships/hooks/useMembershipsHeaderProps';
import { useQueryMemberships } from 'hb-react/shared/apps/Memberships/hooks/useQueryMemberships';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { useMembershipsPurchaseProcess } from './hooks/useMembershipsPurchaseProcess';

const MembershipsIndexContainer = ({
  purchaseStrategyType = MEMBERSHIP_PURCHASE_PROCESS_TYPES.SINGLE,
  appPageConfig = {},
  readOnly = false,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const { flagEvMembershipFoPagination } = useFeatureFlag();

  const resetFiltersRef = useRef();

  const {
    memberships = [],
    user_subscriptions_count: userSubscriptionCount,
    resetFilters,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setSearch,
    indexPath,
  } = useQueryMemberships(undefined, !currentUser);

  const {
    onSelect,
    onCheckout,
    onPurchase,
    onLogin,
    selectedMemberships,
    isDisabled,
  } = useMembershipsPurchaseProcess(purchaseStrategyType);

  const { memberships: membershipsSessionInfo } = useSessionInfoContext();
  const programme = membershipsSessionInfo?.programme;
  const { enable_cumulative_cart: enableCumulativeCart = false } =
    programme || {};
  const { withHeader = true } = appPageConfig;

  const headerProps = useMembershipsHeaderProps({ appPageConfig, programme });

  const onResetFilters = () => {
    resetFilters();
  };

  return (
    <Box flexDirection="column" width="100%">
      {withHeader && <AppHeader {...headerProps} mb={6} />}
      {flagEvMembershipFoPagination && (
        <Box flexDirection="column" width="100%" mb={50}>
          <FiltersApp
            // @ts-expect-error: until FiltersApp gets converted to TS
            config={{
              filters_path: indexPath,
              type: 'memberships',
              handleFiltersUpdate: debounce((membershipSearch) => {
                setSearch(membershipSearch?.query?.search);
              }, 200),
              initialFilters: {},
              staticFilters: {
                top: [
                  {
                    type: 'free_text',
                    name: 'search',
                    label: __('Search for memberships'),
                    placeholder: __('Type a name or keyword'),
                    display: false,
                    user_can_search: true,
                    options: [],
                    section_id: 'top',
                  },
                ],
                overlay: [],
              },
            }}
            activeExternalFilters={{}}
            onResetFilters={onResetFilters}
            resetFiltersRef={resetFiltersRef}
          />
        </Box>
      )}
      <Container pb={50} pt={withHeader ? 0 : 9}>
        <Grid width="100%">
          <Row>
            <Col>
              {currentUser && enableCumulativeCart && (
                <MemberShipTags resetFiltersRef={resetFiltersRef} />
              )}
              {!!userSubscriptionCount && (
                <MembershipsBar count={userSubscriptionCount} />
              )}
              {memberships.length > 0 ? (
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {memberships.map((membership) => (
                      <Box
                        key={membership.id}
                        mb={6}
                        data-testid={`membership-${membership.id}`}
                      >
                        <Membership
                          {...membership}
                          onSelect={onSelect}
                          onPurchase={onPurchase}
                          onLogin={onLogin}
                          programme={programme}
                          disabled={isDisabled?.(membership)}
                          readOnly={readOnly}
                          isPublic={!currentUser}
                        />
                      </Box>
                    ))}
                  </Box>
                  <Box justifyContent="center" mb={5}>
                    {hasNextPage && flagEvMembershipFoPagination && (
                      <Button
                        dataTestId="load-more-memberships"
                        onClick={() => fetchNextPage()}
                        isLoading={isFetchingNextPage}
                      >
                        {s__('Memberships|Load more')}
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : userSubscriptionCount > 0 ? (
                <NoMoreMembershipsToPurchase />
              ) : (
                <EmptyState onResetFilters={onResetFilters} />
              )}
            </Col>
          </Row>
        </Grid>
      </Container>
      {selectedMemberships && !readOnly && (
        <MembershipsFooter
          selectedMemberships={selectedMemberships}
          onCheckout={onCheckout}
        />
      )}
    </Box>
  );
};

const EmptyState = ({ onResetFilters }) => (
  <NoMemberships onResetFilters={onResetFilters} />
);

const MembershipsFooter = ({ onCheckout, selectedMemberships = [] }) => (
  <ActionWrapper py={4} px={5}>
    <Container>
      <Grid width="100%">
        <Row>
          <Col>
            <Box justifyContent="flex-end">
              <Button
                isDisabled={selectedMemberships.length === 0}
                onClick={onCheckout}
              >
                {s__('Memberships|Proceed to payment')}
              </Button>
            </Box>
          </Col>
        </Row>
      </Grid>
    </Container>
  </ActionWrapper>
);

const MembershipsBar = ({ count = 0 }) => {
  const outboxedFontTheme = useOutboxedFontTheme();

  return (
    <ThemeProvider
      theme={outboxedFontTheme.themeName}
      fontBrightness={outboxedFontTheme.fontBrightness}
    >
      <Box
        justifyContent={{ _: 'flex-start', m: 'flex-end' }}
        flexDirection={{ _: 'column', m: 'row' }}
        mb={6}
        data-testid="memberships-bar"
      >
        <Box>
          {s__('Memberships|You have already purchased')}

          <Box fontWeight={1} ml={1} as="span">
            {ns__(
              'Memberships|%{count} membership',
              'Memberships|%{count} memberships',
              count,
            ).i__({
              count,
            })}
          </Box>
        </Box>

        <Link
          to={Routes.me_my_memberships_path()}
          ml={{ _: 0, m: 1 }}
          mt={{ _: 1, m: 0 }}
        >
          {s__('Memberships|Manage your memberships')}
        </Link>
      </Box>
    </ThemeProvider>
  );
};

export default MembershipsIndexContainer;

const NoMoreMembershipsToPurchase = () => (
  <Box display="block" maxWidth={654} margin="0 auto">
    <Card mt={4}>
      <Box mb={3} font="h4-regular">
        {s__(
          'Memberships|You already have the valid memberships available to you, view them on your My Memberships page',
        )}
      </Box>
      <ButtonInLink
        linkProps={{ to: Routes.me_my_memberships_path() }}
        variant="primary"
      >
        {s__('Memberships|My Memberships')}
      </ButtonInLink>
    </Card>
  </Box>
);

const ActionWrapper = styled(Card).attrs(() => ({
  display: 'flex',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
}))`
  z-index: 10;
  border-radius: 0;
`;
